import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { setGoogleTranslateLanguage, GOOGLE_TRANSLATE_LANGUAGES, GoogleTranslateLanguageCode } from '../hooks/useGoogleTranslate';

interface LanguageContextType {
  language: GoogleTranslateLanguageCode;
  setLanguage: (lang: GoogleTranslateLanguageCode) => void;
  t: (key: string) => string;
  translateLanguage: GoogleTranslateLanguageCode;
  setTranslateLanguage: (lang: GoogleTranslateLanguageCode) => void;
  availableLanguages: typeof GOOGLE_TRANSLATE_LANGUAGES;
}

// English-only text map. Google Translate handles all other languages automatically.
const translations: Record<string, string> = {
  // Header
  'header.timings': 'Temple Timings: 6:00 AM – 11:00 AM',
  'header.email': 'info@kottakovilakam.com',
  'header.donate': 'Donate',
  'header.bookPooja': 'Book Pooja',
  'header.templeName': 'Sri Kotta-Kovilakam Kshethram',

  // Notifications
  'notifications.enable': 'Enable Notifications',
  'notifications.enabled': 'Notifications enabled successfully.',
  'notifications.denied': 'Notifications are blocked. Please enable in browser settings.',
  'notifications.unsupported': 'Notifications are not supported on this device/browser.',
  'notifications.missingVapid': 'Missing VAPID key configuration. Contact admin.',
  'notifications.error': 'Could not enable notifications. Please try again.',

  // Menu
  'menu.home': 'Home',
  'menu.poojas': 'Poojas',
  'menu.donate': 'Donate',
  'menu.gallery': 'Gallery',
  'menu.news': 'News / Event',
  'menu.about': 'About',
  'menu.nearby': 'Explore Nearby',
  'menu.contact': 'Contact',

  // Submenu - Poojas
  'submenu.dailyPoojas': 'Daily Poojas',
  'submenu.festivalPoojas': 'Festival Poojas',
  'submenu.specialPoojas': 'Special Poojas',
  'submenu.vazhipadList': 'Vazhipad List',
  'submenu.booking': 'Online Booking',

  // Submenu - Donate
  'submenu.donateOverview': 'Donate Overview',
  'submenu.onlineDonation': 'Online Donation',
  'submenu.annadanam': 'Annadanam',
  'submenu.renovationFund': 'Temple Renovation Fund',
  'submenu.festivalContribution': 'Festival Contribution',

  // Submenu - Gallery
  'submenu.galleryOverview': 'Gallery Overview',
  'submenu.photos': 'Photos',
  'submenu.videos': 'Videos',
  'submenu.events': 'Events',
  'submenu.templePremises': 'Temple Premises',

  // Submenu - News
  'submenu.newsOverview': 'News Overview',
  'submenu.templeNews': 'Temple News',
  'submenu.announcements': 'Announcements',
  'submenu.upcomingEvents': 'Upcoming Events',
  'submenu.pastEvents': 'Past Events',
  'submenu.notices': 'Notices / Circulars',

  // Submenu - About
  'submenu.aboutOverview': 'About Overview',
  'submenu.templeHistory': 'Temple History',
  'submenu.deities': 'Deities',
  'submenu.festivals': 'Festivals',
  'submenu.administration': 'Administration',
  'submenu.rulesTimings': 'Temple Rules & Timings',

  // Submenu - Nearby
  'submenu.nearbyOverview': 'Nearby Overview',
  'submenu.touristPlaces': 'Tourist Places',
  'submenu.beaches': 'Beaches',
  'submenu.boating': 'Boating',
  'submenu.viewpoints': 'Viewpoints',
  'submenu.heritageSites': 'Heritage Sites',
  'submenu.templesNearby': 'Temples Nearby',
  'submenu.activities': 'Activities',

  // Submenu - Contact
  'submenu.contactOverview': 'Contact Overview',
  'submenu.contactInfo': 'Contact Info',
  'submenu.mapDirections': 'Map / Directions',
  'submenu.feedbackForm': 'Feedback Form',
  'submenu.templeOffice': 'Temple Office Numbers',

  // Home Page - About Temple Section
  'home.aboutTemple': 'ABOUT TEMPLE',
  'home.aboutIntro': 'A Brief Introduction',
  'home.aboutDescription': 'Our temple stands as a beacon of devotion and spirituality, serving the community for generations. Experience divine peace and participate in our sacred rituals.',
  'home.learnMore': 'Learn More',
  'home.viewDetails': 'View Details',

  // About Temple Features
  'about.dailyPoojas': 'Daily Poojas',
  'about.dailyPoojasDesc': 'Traditional rituals performed twice daily',
  'about.festivals': 'Festivals',
  'about.festivalsDesc': 'Grand celebrations throughout the year',
  'about.annadanam': 'Annadanam',
  'about.annadanamDesc': 'Free meals served to devotees daily',
  'about.heritage': 'Heritage',
  'about.heritageDesc': '150+ years of divine service',

  // Pooja Centre
  'pooja.centre': 'POOJA CENTRE',
  'pooja.plan': 'Plan Your Devotional Day',
  'pooja.description': 'Browse pooja categories and book your offerings online with instant confirmation.',
  'pooja.book': 'Book a Pooja',
  'pooja.viewVazhipads': 'View Vazhipads',
  'pooja.daily': 'Daily Poojas',
  'pooja.dailyDesc': 'Regular poojas performed every day',
  'pooja.festival': 'Festival Poojas',
  'pooja.festivalDesc': 'Special poojas during festivals',
  'pooja.special': 'Special Poojas',
  'pooja.specialDesc': 'Book custom poojas online',
  'pooja.vazhipad': 'Vazhipad',
  'pooja.vazhipadDesc': 'Traditional offerings list',

  // Gallery
  'gallery.overview': 'GALLERY OVERVIEW',
  'gallery.explore': 'Explore Photos & Videos',
  'gallery.description': 'A glimpse of temple moments — festivals, premises, events and serene views.',
  'gallery.photos': 'Photos',
  'gallery.videos': 'Videos',
  'gallery.events': 'Events',
  'gallery.premises': 'Premises',
  'gallery.curated': 'Curated',
  'gallery.highlights': 'Highlights',
  'gallery.recent': 'Recent',
  'gallery.scenic': 'Scenic',
  'gallery.templeTour': 'Temple Tour',

  // Donation
  'donate.overview': 'DONATION OVERVIEW',
  'donate.support': 'Support Temple Causes',
  'donate.description': 'Your contribution helps maintain the temple and serve the community.',
  'donate.online': 'Online Donation',
  'donate.annadanam': 'Annadanam',
  'donate.renovation': 'Renovation Fund',
  'donate.festival': 'Festival Contribution',
  'donate.now': 'Donate Now',
  'donate.secure': 'Secure Payment',
  'donate.monthly': 'Monthly Donation',
  'donate.oneTime': 'One-Time Gift',

  // Nearby
  'nearby.overview': 'NEARBY OVERVIEW',
  'nearby.explore': 'Explore Nearby Attractions',
  'nearby.description': 'Beaches, temples, viewpoints and heritage spots around the temple.',
  'nearby.beaches': 'Beaches',
  'nearby.temples': 'Temples',
  'nearby.viewpoints': 'Viewpoints',
  'nearby.heritage': 'Heritage Sites',

  // Contact
  'contact.overview': 'CONTACT OVERVIEW',
  'contact.getInTouch': 'Get in Touch',
  'contact.description': 'Reach out to us for any queries, feedback, or spiritual guidance.',
  'contact.info': 'Contact Info',
  'contact.map': 'Map & Directions',
  'contact.feedback': 'Feedback',
  'contact.office': 'Office Hours',

  // Common
  'common.open': 'Open',
  'common.viewMore': 'View More →',
  'common.learnMore': 'Learn More',
  'common.explore': 'Explore',
  'common.readMore': 'Read More',
  'common.bookNow': 'Book Now',
  'common.viewAll': 'View All',
  'common.latest': 'Latest',
  'common.popular': 'Popular',
};

const STORAGE_KEY = 'temple_selected_language';

function getSavedLanguage(): GoogleTranslateLanguageCode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && GOOGLE_TRANSLATE_LANGUAGES.some(l => l.code === saved)) {
      return saved as GoogleTranslateLanguageCode;
    }
  } catch (_) { /* localStorage unavailable */ }
  return 'en';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<GoogleTranslateLanguageCode>(getSavedLanguage);

  // Always returns English text; Google Translate handles translation in the DOM.
  const t = (key: string): string => {
    return translations[key] || key;
  };

  // On mount, re-apply the saved language so Google Translate activates
  useEffect(() => {
    const saved = getSavedLanguage();
    if (saved !== 'en') {
      setGoogleTranslateLanguage(saved);
    }
  }, []);

  const setLanguage = useCallback((lang: GoogleTranslateLanguageCode) => {
    // Save to localStorage BEFORE calling setGoogleTranslateLanguage,
    // because switching to English triggers window.location.reload()
    // and any code after that call will not execute.
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (_) { /* ignore */ }
    setLanguageState(lang);
    setGoogleTranslateLanguage(lang);
  }, []);

  const setTranslateLanguage = useCallback((lang: GoogleTranslateLanguageCode) => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (_) { /* ignore */ }
    setLanguageState(lang);
    setGoogleTranslateLanguage(lang);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translateLanguage: language, setTranslateLanguage, availableLanguages: GOOGLE_TRANSLATE_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
